import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/atoms/button";
import { type DocumentSummary } from "~/api/types";
import { Utils } from "~/api/utils";
import { ProgressBar } from "~/atoms/progress-bar";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";

interface DownloadDatabaseModalProps {
  trigger: ReactNode;
  documents: DocumentSummary[];
}

export const DownloadDatabaseModal = ({
  trigger,
  documents,
}: DownloadDatabaseModalProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(($) => $.commons.download.documents)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.commons.download.description)}
          </DialogDescription>
        </DialogHeader>
        <DownloadDatabase
          documents={documents}
          onClose={() => setIsOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

interface Props {
  documents: DocumentSummary[];
  onClose: () => void;
}

const DownloadDatabase = ({ documents, onClose }: Props) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [progress, setProgress] = useState(0);

  const updateProgress = useCallback((value: number) => setProgress(value), []);

  const downloadDatabase = async () => {
    if (documents.length === 0) {
      toast.info(t(($) => $.toasts.info.noDocumentsFound));
      return;
    }

    try {
      setError(false);
      setIsDownloading(true);
      await Utils.saveZipDocuments(documents, updateProgress);
      onClose();
    } catch {
      setError(true);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isDownloading) {
    return (
      <div className="grid gap-6">
        <ProgressBar value={(progress / Math.max(documents.length, 1)) * 100} />
        <div>
          {t(($) => $.commons.download.downloading)} {progress} /{" "}
          {documents.length}
        </div>
        {error && (
          <div className="text-destructive">
            {t(($) => $.commons.download.error)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <DialogDescription>
        {t(($) => $.library.filters.selectedCount, {
          count: documents.length,
        })}
      </DialogDescription>
      {error && (
        <div className="text-destructive">
          {t(($) => $.commons.download.error)}
        </div>
      )}

      <DialogFooter>
        <Button onClick={downloadDatabase} disabled={isDownloading}>
          {t(($) => $.commons.buttons.download)}
        </Button>
      </DialogFooter>
    </div>
  );
};
